import React, { useState, useEffect } from 'react';
import './CaseSettings.scss';
import { getAllGlobalSettingsData, updateCaseSettings } from '../../../services/api/GlobalSettingsApi';
import { useToast } from '../../../context/ToastContext';
import { useGlobalSettings } from '../../../context/GlobalSettingsContext';
import JusticeLoader from '../../../components/JusticeLoader/JusticeLoader';
import { FaTimes, FaInfoCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const CaseSettings = () => {
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [newColumnName, setNewColumnName] = useState('');
    const [settings, setSettings] = useState({
        allowCustomColumns: false,
        placement: 'tab-viewer',
        columns: []
    });
    const { showToast } = useToast();
    const { globalSettings, setGlobalSettings } = useGlobalSettings();
    const navigate = useNavigate();

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setFetchingData(true);
        try {
            const res = await getAllGlobalSettingsData();
            console.log('Fetched global settings:', res?.data);

            // Check both 'cases' and 'caseSettings' for backward compatibility
            const caseSettings = res?.data?.result?.[0]?.cases || res?.data?.result?.[0]?.caseSettings;

            if (caseSettings) {
                console.log('Case settings found:', caseSettings);
                setSettings({
                    allowCustomColumns: caseSettings.allowCustomColumns ?? false,
                    placement: caseSettings.placement ?? 'tab-viewer',
                    columns: caseSettings.columns ?? []
                });
            } else {
                console.log('No case settings found in response');
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            showToast('Failed to fetch settings', 'error');
        } finally {
            setFetchingData(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const payload = {
                ...settings,
                placement: 'tab-viewer'
            };
            await updateCaseSettings(payload);
            showToast('Settings updated successfully', 'success');

            // Update the GlobalSettingsContext with the new case settings
            setGlobalSettings((prevGlobalSettings) => {
                const updatedSettings = { ...prevGlobalSettings };
                if (!updatedSettings.result) {
                    updatedSettings.result = [{}];
                }
                if (!updatedSettings.result[0]) {
                    updatedSettings.result[0] = {};
                }
                updatedSettings.result[0].cases = settings;
                return updatedSettings;
            });

            // Refresh the data after successful save
            await fetchSettings();
            setTimeout(() => {
                navigate("/home/displayboard");
            }, 300);

        } catch (error) {
            console.error('Error saving settings:', error);
            showToast('Failed to update settings', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        fetchSettings();
    };

    const handleAddColumn = (e) => {
        // Prevent default form submission or page reload behavior
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        const trimmedName = newColumnName.trim();

        if (!trimmedName) {
            showToast('Please enter a column name', 'error');
            return;
        }

        // Prevent duplicate column names
        const isDuplicate = settings.columns.some(
            col => col.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (isDuplicate) {
            showToast('Column name already exists', 'error');
            return;
        }

        setSettings(prev => ({
            ...prev,
            columns: [...prev.columns, { name: trimmedName, datatype: 'text(50)' }]
        }));
        setNewColumnName('');
    };

    const handleRemoveColumn = (index) => {
        setSettings(prev => ({
            ...prev,
            columns: prev.columns.filter((_, i) => i !== index)
        }));
    };

    if (fetchingData) {
        return <JusticeLoader />;
    }

    return (
        <section className="case-settings-section">
            <div className="settings-container">
                <div className="settings-card">
                    <h4 className="card-title">Display Settings</h4>
                    <div className="toggle-row">
                        <label>Display custom columns</label>
                        <div className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={settings.allowCustomColumns}
                                onChange={(e) => setSettings({ ...settings, allowCustomColumns: e.target.checked })}
                                id="allowCustomColumns"
                            />
                            <label htmlFor="allowCustomColumns" className="toggle-slider"></label>
                        </div>
                    </div>
                </div>

                {settings.allowCustomColumns && (
                    <>
                        <div className="settings-card">
                            <h4 className="card-title">Custom Columns</h4>

                            <div className="add-column-container">
                                <input
                                    type="text"
                                    placeholder="Enter column name"
                                    value={newColumnName}
                                    maxLength={40}
                                    onChange={(e) => setNewColumnName(e.target.value)}
                                    className="new-col-input"
                                    // Updated to onKeyDown for reliable Enter key handling
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAddColumn(e);
                                        }
                                    }}
                                />
                                <div className="fixed-datatype">text(50)</div>
                                <button className="add-column-btn" onClick={handleAddColumn}>Add</button>
                            </div>

                            {settings.columns.length > 0 && (
                                <div className="columns-list">
                                    {settings.columns.map((col, index) => (
                                        <div key={index} className="column-item">
                                            <div className="col-info">
                                                <span className="col-number">{index + 1}.</span>
                                                <span className="col-name">{col.name}</span>
                                            </div>
                                            <div className="col-right">
                                                <span className="col-datatype">{col.datatype}</span>
                                                <button className="remove-btn-icon" onClick={() => handleRemoveColumn(index)}>
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* <div className="settings-card">
                            <h4 className="card-title">
                                Placement
                                <div className="info-tooltip-wrapper">
                                    <FaInfoCircle className="info-icon" />
                                    <div className="tooltip-content">
                                        For admin the placement will be tab-viewer only. For non admin users they can view information in tab-viewer or case-details
                                    </div>
                                </div>
                            </h4>
                            <div className="radio-group">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="placement"
                                        value="tab-viewer"
                                        checked={settings.placement === 'tab-viewer'}
                                        onChange={(e) => setSettings({ ...settings, placement: e.target.value })}
                                    />
                                    <span>Tab Viewer</span>
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="placement"
                                        value="case-detail"
                                        checked={settings.placement === 'case-detail'}
                                        onChange={(e) => setSettings({ ...settings, placement: e.target.value })}
                                    />
                                    <span>Case Detail</span>
                                </label>
                            </div>
                        </div> */}
                    </>
                )}

                <div className="action-buttons">
                    <button className="cancel-btn" onClick={handleCancel} disabled={loading}>
                        Cancel
                    </button>
                    <button className="save-btn" onClick={handleSave} disabled={loading}>
                        {loading ? 'Saving...' : 'Save Case Settings'}
                    </button>
                </div>
            </div>
        </section>
    );
};

export default CaseSettings;